@use '../../../scss/global' as *;
@use 'themes';

// ================================================================ //
// -- Local Variables

/**
 * Delay for showing "box-shadow" & hiding "divider line" on body.
 * Value is slightly less than "$transition-durations.short" so that
 * transitions execute seamlessly when "panel collapse" animation is
 * almost complete, making the change imperceptible.
 */
$collapse-transition-delay: 180ms;

/**
 * Padding for both the Context Bar body & "context panel".
 */
%container-padding {
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  padding-left: var(--G-content-padding-left);
  padding-right: var(--G-content-padding-right);
}

/**
 * Box-shadow for both the Context Bar body & "context panel".
 * Shadow is "clipped" on the top, right, & bottom of the container
 * so that it doesn't overlap the Sidebar or Header components.
 */
%container-drop-shadow {
  box-shadow: var(--G-header-box-shadow);
  clip-path: inset(0 0 -100vh 0); // Constrains box-shadow to bottom only
}

// ================================================================ //
// -- Component Styles

:host {
  display: block;

  // ==== Parent Container

  .fluid-page-context-bar {
    min-height: 4em;
    display: flex;
    flex-direction: column;
    position: relative;
    background-color: $white;

    // == Display Density

    &.display-density-compact {
      min-height: unset;

      .context-bar-body,
      .panel-content {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }

  // ==== Context Bar Body (main slot)

  .context-bar-body {
    flex: 1 0 100%;
    display: flex;
    align-items: center;
    column-gap: 0.5em;

    @extend %container-padding;
    @extend %container-drop-shadow;

    // Delay "box-shadow" becoming visible on body until panel has collapsed
    @include transition(
      $properties: 'box-shadow',
      $duration: 0ms,
      $ease: false,
      $delay: $collapse-transition-delay,
    );

    // == Divider Line (between body & "context panel")

    &::after {
      height: 0;
      background-color: $liberty-gray-10;
      display: block;
      position: absolute;
      top: 100%;
      left: var(--G-content-padding-left);
      right: var(--G-content-padding-right);
      content: '';

      // Delay hiding divider until panel has collapsed
      @include transition(
        $properties: 'height',
        $duration: 0ms,
        $ease: false,
        $delay: $collapse-transition-delay,
      );
    }

    // == Open Context Panel

    &.panel-expanded {
      box-shadow: none;

      // Cancel "box-shadow" transition so that it's instantly hidden when panel is open
      @include transition($properties: none);

      &::after {
        height: 1px;

        // Cancel "height" transition so that it's instantly visible when panel is open
        @include transition($properties: none);
      }
    }
  }

  // ==== Body Content

  .body-content {
    flex: 1 0 auto;
  }

  // ==== Collapse Toggle Button

  .collapse-toggle-btn {
    flex: 0 0 auto;
    margin-right: -1em;
  }

  // ==== "Context Panel" Container

  .context-bar-panel-container {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: $white;
    z-index: -1;

    // Transfer "box-shadow" to "context panel" so that it remains visible
    @extend %container-drop-shadow;
  }

  // ==== Context Panel

  .context-bar-panel {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;

    @include transition(
      $properties: 'grid-template-rows',
      $duration: short
    );

    // == Open Context Panel

    &.panel-expanded {
      grid-template-rows: 1fr;
    }
  }

  // ==== "Context Panel" Content Container

  .panel-content-container {
    min-height: 0;
    visibility: hidden;

    @include transition(
      $properties: 'visibility',
      $duration: short
    );

    // == Open Context Panel

    &.content-visible {
      visibility: visible;
    }
  }

  // ==== "Context Panel" Content

  .panel-content {
    @extend %container-padding;
  }
}
